/* eslint-disable camelcase */

import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Actions from 'actions';
import Selectors from 'selectors';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { iconNext, iconBack } from 'assets';
import classnames from 'classnames';
import { ErrorContainer, Button } from 'components/UI';
import { ALL_ACCESS, FREE } from 'utils/constants';

import CONFIG, { isProduction } from 'utils/config';
import { AuthContext } from 'utils/context';
import useQuery from 'hooks/useQuery';
import BasicDetails from './BasicDetails';
import AuthLayout from '../common/AuthLayout';
import WorkDetails from './WorkDetails';
import SocialMediaDetails from './SocialMediaDetails';
import SuccessScreen from './SuccessScreen';
import Activate from '../Activate';
import ChooseTiers from './ChooseTiers';
import { gaEvents, getUTMParameters } from '../../../utils/analytics';
import SubscriptionPlan from './SubscriptionPlan';
import { debounce, isEmpty } from 'lodash';
import PromoCodeWatcher from './PromoCodeWatcher';
import { checkDomainExists } from 'helpers/validateDomain';
import notify from 'utils/notification';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';

const LITE_MEMBER_TYPES = Object.freeze([
  'Musicians',
  'Music School Students',
  'Songwriters',
]);
const MIN_PRO_EXP_YEARS = 6;

const createValidationSchema = (checkAllAccessCriteria) => {
  const RegistrationSchema = [
    Yup.object().shape({
      promoCode: Yup.string().nullable(),
      agree: Yup.bool().oneOf([true], 'Must agree to Terms and Conditions'),
    }),
    Yup.object().shape({
      first_name: Yup.string().required('Required'),
      last_name: Yup.string().required('Required'),
      city: Yup.string().required('Required'),
      country: Yup.string().required('Required'),
      dob: Yup.mixed().required('Required'),
      email: Yup.string().email('Invalid email').required('Required'),
      password: Yup.string().required('Password is required').min(8),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Password must match')
        .required('Confirm password is required'),
      industry_since: Yup.number()
        .required('Required')
        .test(
          'validate all access experience',
          'Must have a minimum of 6 years of experience',
          (value) => {
            const currentYear = new Date().getFullYear();
            const hasAllAccessExp =
              value !== '' && currentYear - Number(value) >= MIN_PRO_EXP_YEARS;
            if (checkAllAccessCriteria) {
              return hasAllAccessExp;
            }
            return true;
          }
        ),
      memberTypes: Yup.array()
        .max(5, 'Maximum 5 member types allowed')
        .required('Required')
        .test(
          'validate all access member type',
          'Must have experience owning a music business',
          (value) => {
            const hasAllAccessMemberType = !!value.find(
              ({ label }) => !LITE_MEMBER_TYPES.includes(label)
            );
            if (checkAllAccessCriteria) {
              return hasAllAccessMemberType;
            }
            return true;
          }
        ),
      company: Yup.string().required('Required'),
      position: Yup.string().required('Required'),
      year_start: Yup.number()
        .required('Required')
        .when('year_end', {
          // allow to display dynamic year_end value, refer to MR 305
          is: ' ',
          then: Yup.number(),
          otherwise: Yup.number().max(
            Yup.ref('year_end'),
            'Invalid start year'
          ),
        }),
      year_end: Yup.mixed().required('Required'),
      company_url: Yup.string()
        .url('Invalid URL')
        .transform((currentValue) => {
          const doesNotStartWithHttp =
            currentValue &&
            !(
              currentValue.startsWith('http://') ||
              currentValue.startsWith('https://')
            );

          if (doesNotStartWithHttp) {
            return `http://${currentValue}`;
          }
          return currentValue;
        })
        .required('Required'),
      facebook: Yup.string().url('Facebook Link must be a valid Url'),
      linked_in: Yup.string().url('LinkedIn Link must be a valid Url'),
      instagram: Yup.string().url('Instagram Link must be a valid Url'),
      source: Yup.string(),
      reason_to_join: Yup.string(),
      referred_by: Yup.string(),
    }),
    Yup.object().shape({
      industry_since: Yup.number()
        .required('Required')
        .test(
          'validate all access experience',
          'Must have a minimum of 6 years of experience',
          (value) => {
            const currentYear = new Date().getFullYear();
            const hasAllAccessExp =
              value !== '' && currentYear - Number(value) >= MIN_PRO_EXP_YEARS;
            if (checkAllAccessCriteria) {
              return hasAllAccessExp;
            }
            return true;
          }
        ),
      memberTypes: Yup.array()
        .max(5, 'Maximum 5 member types allowed')
        .required('Required')
        .test(
          'validate all access member type',
          'Must have experience owning a music business',
          (value) => {
            const hasAllAccessMemberType = !!value.find(
              ({ label }) => !LITE_MEMBER_TYPES.includes(label)
            );
            if (checkAllAccessCriteria) {
              return hasAllAccessMemberType;
            }
            return true;
          }
        ),
      company: Yup.string().required('Required'),
      position: Yup.string().required('Required'),
      year_start: Yup.number()
        .required('Required')
        .when('year_end', {
          // allow to display dynamic year_end value, refer to MR 305
          is: ' ',
          then: Yup.number(),
          otherwise: Yup.number().max(
            Yup.ref('year_end'),
            'Invalid start year'
          ),
        }),
      year_end: Yup.mixed().required('Required'),
      company_url: Yup.string()
        .url('Invalid URL')
        .transform((currentValue) => {
          const doesNotStartWithHttp =
            currentValue &&
            !(
              currentValue.startsWith('http://') ||
              currentValue.startsWith('https://')
            );

          if (doesNotStartWithHttp) {
            return `http://${currentValue}`;
          }
          return currentValue;
        })
        .required('Required'),
    }),
    Yup.object().shape({
      facebook: Yup.string().url('Facebook Link must be a valid Url'),
      linked_in: Yup.string().url('LinkedIn Link must be a valid Url'),
      instagram: Yup.string().url('Instagram Link must be a valid Url'),
    }),
  ];
  return RegistrationSchema;
};

const SignUp = () => {
  const { linkedInTag } = useContext(AuthContext);
  const dispatch = useDispatch();
  const {
    memberTier,
    referralCode: referral_code,
    signupCode: signup_code,
    revised,
    tier,
    billingCycle
  } = useQuery();
  const { selectedPlan: selectedPlanData } = useSubscriptionPlan(billingCycle);
  const isDowngraded = revised === 'true';
  const sourceInfo = getUTMParameters();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [hasTierError, showTierError] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCircle] = useState('year');
  const [referralCode, setReferralCode] = useState(null);
  // const [signupCode, setSignupCode] = useState(null);
  const [liteOnly, setLiteOnly] = useState(false);
  const [userFormValues, setUserFormValues] = useState(null);
  const memberTiers = useSelector(Selectors.memberTiersList);
  const isLoadingTiers = useSelector(
    Selectors.createLoadingSelector([
      Actions.FETCH_MEMBER_TIERS_REQUEST,
      Actions.FETCH_MEMBER_TYPES_REQUEST,
    ])
  );
  const isCreateLoading = useSelector(
    Selectors.createLoadingSelector([Actions.CREATE_USER_REQUEST])
  );

  const selectedTier = memberTiers.find(({ name }) => name === memberTier);
  const selectedTierId = selectedTier?.id;
  const allAccessId = memberTiers.find(({ name }) => name === ALL_ACCESS)?.id;
  const checkAllAccessCriteria = selectedTierId === allAccessId;
  const validationSchema = createValidationSchema(checkAllAccessCriteria);
  const currentValidationSchema = validationSchema[activeStep - 1];
  const showNextScreen = (step = 1) => setActiveStep((currentStep) => currentStep + step)
  const showPrevScreen = () =>
    setActiveStep((currentStep) => {
      if (isFreeTierSelected && currentStep === 2) {
        return 0;
      }
      return Math.max(currentStep - 1, 0)
    });
  const isFreeTierSelected = selectedTierId
    ? memberTiers.find(({ id }) => id === selectedTierId)?.is_free_tier
    : false;

  const isLastStep = isFreeTierSelected && activeStep === 2;

  const isValidPromoCode = !isFreeTierSelected && signup_code && validPromo?.id


  
  // useEffect(() => {
  //   const handler = debounce((code) => {
  //     if (!code) {
  //       setValidPromo(null);
  //       dispatch(Actions.clearPromo());
  //       return
  //     };
  //     dispatch(
  //       Actions.validatePromoCode(
  //         { code, price_id: selectedPlanData?.id },
  //         {
  //           success: (data) => {
  //             setValidPromo(data);
  //           },
  //           failure: (error) => {
  //             setValidPromo(null);
  //             if (error?.response?.status === 404) {
  //               notify('Invalid promo code', 'Promo Code', 'danger');
  //             } else if(code) notify(error?.response?.data?.error || error?.message, 'Promo Code', 'danger');
  //           },
  //         }
  //       )
  //     );
  //   }, 500);

  //   if(selectedPlanData?.id && signup_code) handler(signup_code);

  //   return () => handler.cancel();
  // }, [signup_code, dispatch, selectedPlanData?.id]);
  // if (isFreeTierSelected && activeStep === 1 ) {
  //   setActiveStep(2);
  // }

  // const tierInfo = useSelector(Selectors.memberTier);
  // const [selectedBillingCycle, setPlan] = useState(null);
  const [validPromo, setValidPromo] = useState(null);
  const [errorPromo, setErrorPromo] = useState(null);

  useEffect(() => {
    dispatch(Actions.fetchMemberTier(selectedTierId || tier));
  }, [dispatch, selectedTierId, tier]);

  // useEffect(() => {
  //   if (!isEmpty(tierInfo)) setPlan(tierInfo?.prices[0]?.id);
  // }, [tierInfo]);

  // const isLoadingTier = useSelector(
  //     Selectors.createLoadingSelector([Actions.FETCH_MEMBER_TIER_REQUEST])
  //   );
  // const isLoadingPromoCode = useSelector(
  //   Selectors.createLoadingSelector([Actions.VALIDATE_PROMO_CODE_REQUEST])
  // );

  useEffect(() => {
    if(billingCycle && (billingCycle === 'month' || billingCycle === 'year')) {
      setSelectedBillingCircle(billingCycle);
    }
  }, [billingCycle]);

  useEffect(() => {
    dispatch(Actions.fetchCountryOptions());
    dispatch(Actions.fetchMemberTypes());
    dispatch(Actions.fetchMemberTiers());
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  console.log('activeStep', activeStep);

  useEffect(() => {
    if (!isFreeTierSelected && (billingCycle && (billingCycle === 'month' || billingCycle === 'year'))) {
      // showTierError(false);
      setActiveStep(1);
    } else if (isFreeTierSelected) {
      setActiveStep(2);
    }
  }, [billingCycle, isFreeTierSelected]);

  useEffect(() => {
    if (referral_code) {
      setReferralCode(referral_code);
    }
  }, [referral_code]);

  // useEffect(() => {
  //   if (signup_code) {
  //     setSignupCode(signup_code);
  //   }
  // }, [signup_code]);

  const getPrice = (tier, interval) =>
    Number(tier?.prices?.find((price) => price.interval === interval)?.amount);

  const getMinPrice = (prices) =>
    prices.length ? Math.min(...prices.map(({ amount }) => amount)) : 0;

  async function handleOnSubmit(formValues, actions) {
    setError(null);
    const userDetails = {
      ...formValues,
      referral_code: referralCode,
      signup_code: isValidPromoCode ? signup_code : '',
      member_tier_id: selectedTierId,
      member_types: formValues?.memberTypes?.map(({ id, value }) => ({
        id,
        name: value,
      })),
      source: sourceInfo?.source,
      content: sourceInfo?.content,
      term: sourceInfo?.term,
      medium: sourceInfo?.medium,
      campaign: sourceInfo?.campaign,
    };

    if(activeStep === 2) {
      const domainExists = await checkDomainExists(formValues.company_url);
      if (!domainExists) {
        setError('Domain is not available');
        return;
      }

      const emailValid = await new Promise((resolve) => {
        dispatch(
          Actions.validateEmail(formValues.email, {
            success: () => resolve(true),
            failure: (e) => {
              setError(e);
              resolve(false);
            },
          })
        );
      });
      if (!emailValid) return;
    }


    // if (isLastStep && !selectedTier) return showTierError(true);

    actions.setTouched({});
    actions.setSubmitting(false);
    setUserFormValues(userDetails);

    if (isFreeTierSelected && activeStep === 2) {
      if (isProduction && linkedInTag) {
        linkedInTag.track(CONFIG.LINKEDIN_FREE_TIER);
      }

      return dispatch(
        Actions.createUser(userDetails, {
          success: () => {
            setActiveStep(3);
            gaEvents.conversion(memberTier);
          },
          failure: (e) => {
            setActiveStep(0);
            const { error } = e.response.data;
            setError(
              typeof error === 'string'
                ? error
                : 'An error occurred, please try again later'
            );
          },
        })
      );
    }

    showNextScreen();
  }

  const createUser = (subscriptionParams) => {
    const userDetails = {
      ...userFormValues,
      referral_code: referralCode,
      signup_code: isValidPromoCode ? signup_code : '',
      member_tier_id: selectedTierId,
      member_types: userFormValues?.memberTypes?.map(({ id, value }) => ({
        id,
        name: value,
      })),
      source: sourceInfo?.source,
      content: sourceInfo?.content,
      term: sourceInfo?.term,
      medium: sourceInfo?.medium,
      campaign: sourceInfo?.campaign,
    };

    dispatch(
      Actions.createUser(userDetails, {
        success: () => {
          dispatch(Actions.createSubscription(subscriptionParams));
          gaEvents.conversion(memberTier);
        },
        failure: (e) => {
          setActiveStep(0);
          const { error } = e.response.data;
          setError(
            typeof error === 'string'
              ? error
              : 'An error occurred, please try again later'
          );
        },
      })
    );
  };


  if (activeStep === 0) {
    return (
      <ChooseTiers
        memberTiers={memberTiers.filter(({ name }) => name === ALL_ACCESS || name === FREE)}
        selectedTier={selectedTierId}
        setLiteOnly={setLiteOnly}
        liteOnly={liteOnly}
        getPrice={getPrice}
        getMinPrice={getMinPrice}
        showNextScreen={showNextScreen}
        selectedBillingCycle={selectedBillingCycle}
        setSelectedBillingCircle={setSelectedBillingCircle}
      />
    );
  }

  function renderStepContent(step) {
    switch (step) {
      case 1:
        return (
          <SubscriptionPlan
            billingCycle={billingCycle}
            navigateBack={showPrevScreen}
            selectedTier={selectedTier}
            isLoadingTiers={isLoadingTiers}
            referralCode={referralCode}
            errorPromo={errorPromo}
          />
        )
      case 2:
        return (
          <BasicDetails
            memberTier={memberTiers.find(({ id }) => id === selectedTierId)}
            getPrice={getPrice}
            selectedPlan={billingCycle}
            showPrevScreen={showPrevScreen}
            isFreeTier={isFreeTierSelected}
            validPromo={validPromo}
          />
        );
      // case 3:
      //   return <WorkDetails />;
      // case 4:
      //   return (
      //     <SocialMediaDetails
      //       signupCode={signupCode}
      //       hasTierError={hasTierError}
      //       isLoading={isCreateLoading}
      //     />
      //   );
      default:
        return <div>Not Found</div>;
    }
  }

  const FormButtons = () => (
    <div className="flex justify-between items-center">
      <button
        type="button"
        className={classnames(
          'uppercase text-dusty hover:text-easter flex focus:outline-none',
          { invisible: activeStep <= 0 }
        )}
        onClick={() => {
          showPrevScreen();
          setError(null);
        }}>
        <img alt="check" src={iconBack.default} className="h-4 mt-1 mr-4" />
        Back
      </button>
      {isLastStep ? (
        <Button
          className="max-w-80"
          label="APPLY FOR MEMBERSHIP"
          type="submit"
          disabled={isCreateLoading}
          isLoading={isCreateLoading}
        />
      ) : (
        <button
          type="submit"
          className="uppercase text-dusty hover:text-easter flex focus:outline-none">
          Continue
          <img alt="check" src={iconNext.default} className="h-4 mt-1 ml-4" />
        </button>
      )}
    </div>
  );

  if (!isFreeTierSelected && activeStep === 3) {
    return (
      <Activate
        selectedTier={selectedTierId}
        navigateBack={showPrevScreen}
        createUser={createUser}
        email={userFormValues.email}
        liteOnly={liteOnly}
        referralCode={referralCode}
        signupCode={signup_code}
        billingCycle={selectedBillingCycle}
        validPromo={validPromo}
      />
    );
  }
    

  return (
    <AuthLayout
      progress={activeStep}
      isLoading={isLoadingTiers}
      disableScroll={false}>
      {(isFreeTierSelected && activeStep === 3) ? (
        <SuccessScreen />
      ) : (
        <Formik
          initialValues={
            userFormValues
              ? { ...userFormValues }
              : {
                  first_name: '',
                  last_name: '',
                  email: '',
                  dob: null,
                  country: '',
                  city: '',
                  industry_since: '',
                  memberTypes: [],
                  company: '',
                  position: '',
                  year_start: '',
                  year_end: null,
                  company_url: '',
                  facebook: '',
                  linked_in: '',
                  instagram: '',
                  member_tier_id: null,
                  password: '',
                  confirmPassword: '',
                  promoCode: isValidPromoCode ? signup_code : '',
                  fromSource: '',
                  agree: false,
                  reason_to_join: '',
                  referred_by: '',
                }
          }
          validationSchema={currentValidationSchema}
          onSubmit={handleOnSubmit}>
          {({ handleSubmit, values }) => (
            <form onSubmit={handleSubmit}>
              <PromoCodeWatcher 
                selectedTier={selectedTier} 
                setValidPromo={setValidPromo} 
                setErrorPromo={setErrorPromo} 
                billingCycle={billingCycle} 
              />
              {renderStepContent(activeStep)}
              <ErrorContainer error={error} className="mb-4" />
              <FormButtons />
            </form>
          )}
        </Formik>
      )}
    </AuthLayout>
  );
};

SignUp.propTypes = {};

export default SignUp;
